import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CartSidebar = ({ isOpen, onClose, cartItems, onUpdateQuantity, onRemoveItem, onCheckout }) => {
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);

  const subtotal = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
  const shipping = subtotal > 75 ? 0 : 9.99;
  const promoDiscount = appliedPromo ? subtotal * 0.1 : 0;
  const bulkDiscount = subtotal > 200 ? subtotal * 0.05 : 0;
  const total = subtotal + shipping - promoDiscount - bulkDiscount;

  const handleApplyPromo = () => {
    if (promoCode?.toLowerCase() === 'clean10') {
      setAppliedPromo({ code: 'CLEAN10', discount: 0.1 });
      setPromoCode('');
    } else {
      alert('Invalid promo code');
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
  };

  const getRecommendedProducts = () => {
    return [
      {
        id: 'rec1',
        name: 'Microfiber Cloths Set',
        price: 12.99,
        image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=200&fit=crop'
      },
      {
        id: 'rec2',
        name: 'All-Purpose Cleaner',
        price: 8.99,
        image: 'https://images.unsplash.com/photo-1563453392212-326f5e854473?w=300&h=200&fit=crop'
      }
    ];
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}
      {/* Cart Sidebar */}
      <div className={`
        fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-elevation-5 z-50
        transform transition-transform duration-300
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">
            Shopping Cart ({cartItems?.length})
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          {cartItems?.length === 0 ? (
            /* Empty Cart */
            (<div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Icon name="ShoppingCart" size={32} color="var(--color-text-secondary)" />
              </div>
              <h3 className="text-lg font-medium text-text-primary mb-2">
                Your cart is empty
              </h3>
              <p className="text-text-secondary mb-6">
                Add some products to get started
              </p>
              <Button
                variant="default"
                onClick={onClose}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Continue Shopping
              </Button>
            </div>)
          ) : (
            <>
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {cartItems?.map((item) => (
                  <div key={`${item?.id}-${item?.variant?.id || 'default'}`} className="flex items-start space-x-3 p-3 bg-surface rounded-lg border border-border">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <Image
                        src={item?.image}
                        alt={item?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-sm font-medium text-text-primary truncate">
                            {item?.name}
                          </h4>
                          {item?.variant && (
                            <span className="text-xs text-text-secondary">
                              {item?.variant?.name}
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onRemoveItem(item?.id, item?.variant?.id)}
                          className="text-text-secondary hover:text-error w-6 h-6"
                        >
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => onUpdateQuantity(item?.id, item?.variant?.id, item?.quantity - 1)}
                            className="w-6 h-6"
                            disabled={item?.quantity <= 1}
                          >
                            <Icon name="Minus" size={12} />
                          </Button>
                          <span className="text-sm font-medium text-text-primary w-8 text-center">
                            {item?.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => onUpdateQuantity(item?.id, item?.variant?.id, item?.quantity + 1)}
                            className="w-6 h-6"
                          >
                            <Icon name="Plus" size={12} />
                          </Button>
                        </div>
                        <span className="text-sm font-semibold text-text-primary">
                          ${(item?.price * item?.quantity)?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Recommended Products */}
                {cartItems?.length > 0 && (
                  <div className="pt-4 border-t border-border">
                    <h4 className="font-medium text-text-primary mb-3">
                      You might also like
                    </h4>
                    <div className="space-y-2">
                      {getRecommendedProducts()?.map((product) => (
                        <div key={product?.id} className="flex items-center space-x-3 p-2 bg-surface rounded border border-border">
                          <div className="w-12 h-12 rounded overflow-hidden bg-muted flex-shrink-0">
                            <Image
                              src={product?.image}
                              alt={product?.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-sm font-medium text-text-primary truncate">
                              {product?.name}
                            </h5>
                            <span className="text-sm font-semibold text-text-primary">
                              ${product?.price}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Plus"
                          >
                            Add
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Cart Footer */}
              <div className="border-t border-border p-4 space-y-4">
                {/* Promo Code */}
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <Input
                      type="text"
                      placeholder="Promo code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e?.target?.value)}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      onClick={handleApplyPromo}
                      disabled={!promoCode}
                    >
                      Apply
                    </Button>
                  </div>
                  {appliedPromo && (
                    <div className="flex items-center justify-between bg-success/10 border border-success/20 rounded p-2">
                      <div className="flex items-center space-x-2">
                        <Icon name="Tag" size={14} color="var(--color-success)" />
                        <span className="text-sm font-medium text-success">
                          {appliedPromo?.code} applied
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemovePromo}
                        className="text-success hover:text-success/80"
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>

                {/* Order Summary */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Subtotal</span>
                    <span className="font-medium">${subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Shipping</span>
                    <span className="font-medium">
                      {shipping === 0 ? 'Free' : `$${shipping?.toFixed(2)}`}
                    </span>
                  </div>
                  {promoDiscount > 0 && (
                    <div className="flex justify-between text-success">
                      <span>Promo Discount</span>
                      <span>-${promoDiscount?.toFixed(2)}</span>
                    </div>
                  )}
                  {bulkDiscount > 0 && (
                    <div className="flex justify-between text-success">
                      <span>Bulk Discount (5%)</span>
                      <span>-${bulkDiscount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                    <span>Total</span>
                    <span>${total?.toFixed(2)}</span>
                  </div>
                </div>

                {/* Free Shipping Notice */}
                {shipping > 0 && (
                  <div className="bg-warning/10 border border-warning/20 rounded p-3">
                    <div className="flex items-center space-x-2">
                      <Icon name="Truck" size={16} color="var(--color-warning)" />
                      <span className="text-sm text-warning font-medium">
                        Add ${(75 - subtotal)?.toFixed(2)} more for free shipping!
                      </span>
                    </div>
                  </div>
                )}

                {/* Checkout Button */}
                <Button
                  variant="default"
                  fullWidth
                  onClick={onCheckout}
                  iconName="CreditCard"
                  iconPosition="left"
                  className="font-medium"
                >
                  Proceed to Checkout
                </Button>

                {/* Continue Shopping */}
                <Button
                  variant="outline"
                  fullWidth
                  onClick={onClose}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Continue Shopping
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;